import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ChevronLeft, LucideIconData, RefreshCcw, X } from 'lucide-angular';

import { CompareService } from '../../shared/services/compare.service';
import {
  LOW_STOCK_THRESHOLD,
  Product,
  VARIANT_LABELS,
} from '../../shared/models/product.model';
import { ButtonComponent } from '../../shared/ui/button/button.component';
import { IconComponent } from '../../shared/ui/icon/icon.component';
import { VariantBadgeComponent } from '../../shared/ui/variant-badge/variant-badge.component';
import {
  calcDiscountPercent,
  displayLanguage,
  formatCondition,
  formatOriginalPrice,
  formatPrice,
  formatProductType,
  isSingleProduct,
  stripHtml,
} from '../../shared/utils/product.utils';
import {
  DisplayCategory,
  pickDisplayCategory,
} from '../../shared/utils/category.utils';

interface TableRow {
  key: string;
  label: string;
  values: (string | null)[];
  diffs: boolean[];
}

type RowDef = {
  key: string;
  label: string;
  extract: (p: Product) => string | null;
};

const FIXED_ROWS: RowDef[] = [
  {
    key: 'price',
    label: 'Precio',
    // Productos agotados llegan sin precio desde el back; renderiza "—"
    // (el null se interpola así en el template, L135).
    extract: (p) =>
      (p.stock ?? 0) === 0 && !p.isPreSale ? null : formatPrice(p),
  },
  {
    key: 'rarity',
    label: 'Rareza',
    extract: (p) => (isSingleProduct(p) ? (p.rarity ?? null) : null),
  },
  {
    key: 'condition',
    label: 'Condición',
    // `toUiProduct` ya descarta la `quality` heredada de los no-singles, así
    // que aquí basta con leer `condition` sin releer el crudo de la API.
    extract: (p) => (isSingleProduct(p) ? formatCondition(p.condition ?? null) : null),
  },
  {
    key: 'productType',
    label: 'Tipo de producto',
    extract: (p) => formatProductType(p.productType ?? p.product_type),
  },
  {
    key: 'cardType',
    label: 'Tipo de carta',
    extract: (p) => (isSingleProduct(p) ? (p.cardType ?? null) : null),
  },
  {
    key: 'hp',
    label: 'HP',
    extract: (p) => (isSingleProduct(p) ? (p.hp ?? null) : null),
  },
  {
    key: 'stage',
    label: 'Stage',
    extract: (p) => (isSingleProduct(p) ? (p.stage ?? null) : null),
  },
  // Mecánicas de juego (OP/MTG). Cada fila se oculta cuando todas las celdas
  // son null/'' (filtro en `buildRow` + `hasValue`), por lo que comparar dos
  // cartas Pokémon no añade ruido. power/toughness se dejan separados aquí
  // (no combinados como en quickview/PDP) para que la tabla side-by-side
  // alinee dato por dato y un toughness ausente sea visualmente evidente.
  {
    key: 'color',
    label: 'Color',
    extract: (p) => (isSingleProduct(p) ? (p.color ?? null) : null),
  },
  {
    key: 'cost',
    label: 'Coste',
    extract: (p) => (isSingleProduct(p) ? (p.cost ?? null) : null),
  },
  {
    key: 'mana_cost',
    label: 'Maná',
    extract: (p) => (isSingleProduct(p) ? (p.mana_cost ?? null) : null),
  },
  {
    key: 'power',
    label: 'Poder',
    extract: (p) => (isSingleProduct(p) ? (p.power ?? null) : null),
  },
  {
    key: 'toughness',
    label: 'Resistencia',
    extract: (p) => (isSingleProduct(p) ? (p.toughness ?? null) : null),
  },
  {
    key: 'counter',
    label: 'Counter',
    extract: (p) => (isSingleProduct(p) ? (p.counter ?? null) : null),
  },
  {
    key: 'card_attribute',
    label: 'Atributo',
    extract: (p) => (isSingleProduct(p) ? (p.card_attribute ?? null) : null),
  },
  {
    key: 'subtype',
    label: 'Subtipo',
    extract: (p) => (isSingleProduct(p) ? (p.subtype ?? null) : null),
  },
  {
    key: 'artist',
    label: 'Artista',
    extract: (p) => (isSingleProduct(p) ? (p.artist ?? null) : null),
  },
  {
    key: 'collectorNumber',
    label: 'Collector number',
    extract: (p) => (isSingleProduct(p) ? (p.collectorNumber ?? null) : null),
  },
  { key: 'language', label: 'Idioma', extract: (p) => displayLanguage(p) },
  {
    key: 'foil',
    label: 'Foil',
    extract: (p) =>
      isSingleProduct(p) && typeof p.is_foil === 'boolean'
        ? (p.is_foil ? 'Sí' : 'No')
        : null,
  },
  {
    key: 'holo',
    label: 'Holo',
    extract: (p) =>
      isSingleProduct(p) && typeof p.is_holo === 'boolean'
        ? (p.is_holo ? 'Sí' : 'No')
        : null,
  },
  { key: 'brand', label: 'Marca', extract: (p) => p.brand ?? null },
  {
    key: 'releaseDate',
    label: 'Fecha de lanzamiento',
    extract: (p) => p.releaseDate ?? null,
  },
  { key: 'contents', label: 'Contenido', extract: (p) => p.contents ?? null },
  {
    key: 'shortDescription',
    label: 'Descripción',
    extract: (p) =>
      p.shortDescription ? stripHtml(p.shortDescription) : null,
  },
];

function hasValue(v: string | null): boolean {
  return v !== null && v.trim() !== '';
}

function buildRow(def: RowDef, items: Product[]): TableRow {
  const values = items.map((p) => {
    const v = def.extract(p);
    return hasValue(v) ? v : null;
  });
  const norm = values.map((v) => (v ?? '').toString().trim().toLowerCase());
  const diffs = norm.map((v, i) =>
    norm.some((w, j) => j !== i && w !== v),
  );
  return { key: def.key, label: def.label, values, diffs };
}

function buildSpecRows(items: Product[]): TableRow[] {
  const keys = new Set<string>();
  for (const p of items) {
    if (p.specs) for (const k of Object.keys(p.specs)) keys.add(k);
  }
  return Array.from(keys).map((k) =>
    buildRow(
      {
        key: `spec:${k}`,
        label: k.charAt(0).toUpperCase() + k.slice(1),
        extract: (p) => p.specs?.[k] ?? null,
      },
      items,
    ),
  );
}

@Component({
  selector: 'app-compare-page',
  imports: [
    ButtonComponent,
    IconComponent,
    VariantBadgeComponent,
    RouterLink,
  ],
  templateUrl: './compare-page.component.html',
  styleUrl: './compare-page.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ComparePageComponent {
  private readonly compareService = inject(CompareService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly items = this.compareService.items;
  protected readonly ids = this.compareService.ids;
  protected readonly loading = this.compareService.loading;
  protected readonly error = this.compareService.error;
  protected readonly missingIds = this.compareService.missingIds;
  protected readonly unavailableIds = this.compareService.unavailableIds;
  protected readonly count = this.compareService.count;
  protected readonly maxItems = this.compareService.maxItems;

  protected readonly closeIcon: LucideIconData = X;
  protected readonly retryIcon: LucideIconData = RefreshCcw;
  protected readonly backIcon: LucideIconData = ChevronLeft;

  private readonly syncingFromUrl = signal(false);

  protected readonly hasMissing = computed(() => this.missingIds().length > 0);
  protected readonly hasUnavailable = computed(
    () => this.unavailableIds().length > 0,
  );

  protected readonly categories = computed<(DisplayCategory | null)[]>(() =>
    this.items().map((p) => pickDisplayCategory(p.categories)),
  );

  protected readonly isColOutOfStock = computed(() =>
    this.items().map((p) =>
      p.isPreSale ? (p.prepurchase_stock ?? 0) === 0 : (p.stock ?? 0) === 0,
    ),
  );

  protected readonly formattedPrices = computed(() =>
    this.items().map((p) => formatPrice(p)),
  );

  protected readonly formattedOriginalPrices = computed(() =>
    this.items().map((p) =>
      p.isOnSale && p.originalPrice != null ? formatOriginalPrice(p) : null,
    ),
  );

  protected readonly discounts = computed(() =>
    this.items().map((p) => calcDiscountPercent(p)),
  );

  protected readonly stockLabels = computed(() =>
    this.items().map((p) => {
      if (p.stock === 0) return { label: 'Agotado', tone: 'error' as const };
      if (p.stock <= LOW_STOCK_THRESHOLD)
        return { label: `Últimas ${p.stock} unidades`, tone: 'warning' as const };
      return { label: `${p.stock} unidades`, tone: 'muted' as const };
    }),
  );

  protected readonly variantLabels = computed(() =>
    this.items().map((p) =>
      isSingleProduct(p) ? (VARIANT_LABELS[p.variant] ?? null) : null,
    ),
  );

  protected readonly rows = computed<TableRow[]>(() => {
    const items = this.items();
    if (items.length === 0) return [];
    const fixed = FIXED_ROWS.map((def) => buildRow(def, items));
    const specs = buildSpecRows(items);
    return [...fixed, ...specs].filter((r) =>
      r.values.some((v) => v !== null),
    );
  });

  constructor() {
    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const raw = params.get('ids');
        if (raw === null) {
          // URL vacía: no pisamos selección existente (permite volver a /comparar
          // desde la barra sin perder nada).
          this.triggerFetch();
          return;
        }
        const urlIds = raw
          .split(',')
          .map((s) => Number(s.trim()))
          .filter((n) => Number.isFinite(n) && n > 0);
        const current = this.ids();
        if (!sameArray(urlIds, current)) {
          // URL > sessionStorage. Sobrescribimos service con ids de la URL.
          this.syncingFromUrl.set(true);
          this.compareService.hydrateFromIds(urlIds);
          this.syncingFromUrl.set(false);
        }
        this.triggerFetch();
      });

    // Cuando el usuario quita una columna desde la tabla, reflejarlo en la URL.
    effect(() => {
      const ids = this.ids();
      if (this.syncingFromUrl()) return;
      const currentUrlParam = this.route.snapshot.queryParamMap.get('ids');
      const nextParam = ids.length > 0 ? ids.join(',') : null;
      if (currentUrlParam === nextParam) return;
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { ids: nextParam },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    });
  }

  protected trackRow = (_i: number, row: TableRow): string => row.key;
  protected trackItemId = (_i: number, p: Product): number | string => p.id;

  protected onRemove(id: number | string): void {
    this.compareService.removeById(id);
  }

  protected onRetry(): void {
    this.triggerFetch();
  }

  protected onClear(): void {
    this.compareService.clear();
  }

  protected productHref(p: Product): string[] {
    const slug = p.slug ?? String(p.id);
    const category =
      p.categories?.find((c) => c.slug)?.slug ?? 'producto';
    return ['/tienda', category, slug];
  }

  private triggerFetch(): void {
    const ids = this.ids();
    const loaded = this.items().map((p) => Number(p.id));
    // Evita refetch cuando el usuario quita una columna: el service ya
    // actualizó _items localmente y la URL se sincronizó — los datos
    // cargados siguen siendo válidos.
    if (ids.length > 0 && sameArray(ids, loaded)) return;
    this.compareService
      .fetchDetails()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ error: () => { /* error ya capturado en el service */ } });
  }
}

function sameArray(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}
